import React, { useEffect, useRef } from 'react'
import './profile-icon.styles.scss'

// Uploaded photos are cropped to a centered square this many pixels wide.
const ICON_SIZE = 256;

/**
 * Crops an image file to a centered square and returns it as a JPEG blob.
 * Rejects when the browser can't read the image (e.g. HEIC outside Safari).
 */
function squareBlob(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const side = Math.min(img.naturalWidth, img.naturalHeight);
            const canvas = document.createElement('canvas');
            canvas.width = ICON_SIZE;
            canvas.height = ICON_SIZE;
            canvas.getContext('2d').drawImage(img,
                (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2,
                side, side, 0, 0, ICON_SIZE, ICON_SIZE);
            URL.revokeObjectURL(url);
            canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('crop failed')),
                'image/jpeg', 0.9);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('unreadable image'));
        };
        img.src = url;
    });
}

/**
 * The profile picture picker. `icon` is {url, blob?}: a saved or Google photo
 * URL, or a cropped upload waiting to be saved. Empty means the initial.
 */
const ProfileIcon = ({ name, icon, photoURL, onChange, onError }) => {

    const fileRef = useRef();

    // Free the preview of an upload once it's replaced.
    useEffect(() => {
        if (!icon.blob) return;
        return () => URL.revokeObjectURL(icon.url);
    }, [icon])

    async function handleUpload(file) {
        fileRef.current.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            onError('Please choose an image file.');
            return;
        }
        try {
            const blob = await squareBlob(file);
            onChange({ url: URL.createObjectURL(blob), blob });
        } catch {
            onError("That image couldn't be opened. Try a JPEG or PNG.");
        }
    }

    const initial = (name ?? '').trim().charAt(0).toUpperCase() || '?';

    return (
        <div className='profile-icon-container'>
            <label>Profile picture <span className='optional'>(optional)</span></label>
            <div className='profile-icon-row'>
                {icon.url
                    ? <img className='profile-image' src={icon.url} alt='' referrerPolicy='no-referrer'
                        onError={() => onChange({})} />
                    : <span className='profile-image profile-initial'>{initial}</span>
                }
                <div className='image-buttons'>
                    <input type='file' accept='image/*' ref={fileRef}
                        onChange={(e) => handleUpload(e.target.files[0])} />
                    <button className='g-button' type='button' onClick={() => fileRef.current.click()}>
                        Upload a photo
                    </button>
                    {(photoURL && icon.url !== photoURL) &&
                        <button className='g-button' type='button' onClick={() => onChange({ url: photoURL })}>
                            Use my account photo
                        </button>
                    }
                    {icon.url &&
                        <button className='g-button' type='button' onClick={() => onChange({})}>
                            Use my initial
                        </button>
                    }
                </div>
            </div>
        </div>
    )
}

export default ProfileIcon
